/**
 * Application Entry Point
 * Renders the main App component with proper React 18 setup and configuration management
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { ConfigProvider } from './components/ConfigProvider';
import ConfigLoader from './components/ConfigLoader';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import '@aws-amplify/ui-react/styles.css';
// Self-hosted Inter / Space Grotesk / IBM Plex Mono (CMO audit 2026-08-15).
// These used to be fetched from Google's font CDNs, which put an undisclosed
// third party in the request path and leaked veteran IPs on every page load.
// Imported first so the @font-face rules are registered before tokens.css
// names the families. See styles/fonts.css for the full rationale.
import './styles/fonts.css';
// Design System v2 foundation - tokens only (pure :root vars, no side effects).
// This exposes --as-* CSS variables globally so uplifted pages reference
// them instead of hardcoded hex, making palette/type/radius changes
// one-file swaps. base.css primitives (.as-btn/.as-card/.as-chip) are NOT
// imported globally to avoid collisions with MUI's CssBaseline.
import './styles/tokens.css';
// v1.1 primitives - class-only recipes (.as-badge/.as-card-flat/.as-advocate-rule/
// .as-section-label/.as-eyebrow/.as-skeleton/.as-tnum). Safe to import globally:
// no element selectors or reset, so it cannot fight CssBaseline. Opt-in per
// component; nothing changes until a class is applied.
import './styles/primitives.css';

// Get the root element
const container = document.getElementById('root');
const root = createRoot(container);

// Render the application with configuration management
root.render(
  <React.StrictMode>
    <ConfigProvider>
      <ConfigLoader>
        <App />
      </ConfigLoader>
      {/* Global, auth-independent: prompts a reload when a newer build deploys. */}
      <UpdateAvailableToast />
    </ConfigProvider>
  </React.StrictMode>
);

// Web Vitals for performance monitoring (optional)
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
import('./reportWebVitals').then(({ default: reportWebVitals }) => {
  reportWebVitals();
}).catch(() => {
  // Optional web vitals import failed - not critical
});